import type { RichEditorImageUploadHandler, TiptapDoc } from '@odo/shared-ui';
import type { EventDraft } from './eventDraft';

// Shared by EventForm and the section components it renders. They live here
// rather than in EventForm because a section importing them from EventForm
// while EventForm imports the section is a cycle: the section evaluates first
// and reads these consts inside their temporal dead zone.

export const INPUT_CLASS =
    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm text-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';

export const FIELD_LABEL_CLASS = 'block text-[13px] font-semibold text-gray-700 mb-1.5';

export const SECTION_TITLE_CLASS =
    'text-[13px] font-bold tracking-[0.06em] text-gray-800 uppercase pb-2 border-b border-gray-200';

// A lighter-weight heading for a group of fields *inside* a merged
// `EventFormSection` panel (e.g. "Banner" and "Pop-up" within Content) -
// `SECTION_TITLE_CLASS`'s border/weight is already spent on the section's
// own collapsible header, so a subsection reads at one visual step down.
export const SUBSECTION_LABEL_CLASS =
    'text-[11px] font-bold tracking-[0.04em] text-gray-400 uppercase';

// So RichEditor consumers can mount controlled before the admin types.
export const EMPTY_TIPTAP_DOC: TiptapDoc = { type: 'doc', content: [{ type: 'paragraph' }] };

export type FieldSetter = <K extends keyof EventDraft>(key: K, value: EventDraft[K]) => void;

export interface SectionProps {
    draft: EventDraft;
    set: FieldSetter;
    /** Its own setter: changing kind rewrites other fields (see `draftWithKind`). */
    setKind: (kind: string) => void;
    errors: Record<string, string>;
    touched: boolean;
    isEdit: boolean;
    /** The draft arrived pre-filled from a source event - editing or duplicating.
     *  Distinct from `isEdit`: a duplicate is a new event with seeded fields. */
    seeded: boolean;
    canManage: boolean;
    /** `kind === 'announcement'`: no join link, action, countdown or email. */
    isAnnouncement: boolean;
    /** Uploads an image dropped/pasted into the body editor. */
    onImageUpload: RichEditorImageUploadHandler;
}
